<template>
	<div class="uploader-container">
		<form @submit.prevent="handleSubmit">
			<div class="form-group">
				<label>סוג הורדה:</label>
				<div class="content-mode-selector">
					<button v-for="mode in contentModes" :key="mode.value" type="button" class="mode-button"
						:class="{ active: selectedContentMode === mode.value }" :disabled="isLoading"
						@click="setContentMode(mode.value)">
						<strong>{{ mode.label }}</strong>
						<span>{{ mode.description }}</span>
					</button>
				</div>
				<p class="field-hint">{{ getModeHint() }}</p>
			</div>

			<div class="form-group">
				<label for="url">{{ getUrlLabel() }}</label>
				<input type="text" id="url" v-model="videoUrl" :placeholder="getUrlPlaceholder()" :disabled="isLoading"
					required @input="resetCheckStatus" />
			</div>

			<div class="form-options">
				<div class="form-group format-group">
					<label for="formatType">סוג פורמט:</label>
					<select id="formatType" v-model="formatType" :disabled="isLoading">
						<option value="video">וידאו</option>
						<option value="audio">אודיו בלבד</option>
					</select>
				</div>

				<div class="form-group quality-group" v-if="formatType === 'video'">
					<label for="qualityPreference">איכות:</label>
					<select id="qualityPreference" v-model="qualityPreference" :disabled="isLoading">
						<option value="best">הטובה ביותר</option>
						<option value="1080p">1080p</option>
						<option value="720p">720p</option>
						<option value="480p">480p</option>
						<option value="360p">360p</option>
					</select>
				</div>
			</div>

			<div class="cookies-upload-section">
				<p class="cookie-sync-notice">
					אם נבחר קובץ ידני, הוא יישלח מחדש מיד לפני ההורדה. אם לא נבחר קובץ, התוסף שלנו יסנכרן
					אוטומטית את עוגיות youtube.com לפני שליחת בקשת ההורדה.
				</p>
				<label for="youtubeCookies">קובץ cookies ידני (אופציונלי):</label>
				<input id="youtubeCookies" ref="youtubeCookiesInput" class="cookies-file-input" type="file"
					accept=".txt,text/plain" :disabled="isLoading || isCookiesUploading"
					@change="handleCookiesSelection" />
				<p class="field-hint">
					הקובץ חייב להיות בפורמט Netscape, להתחיל ב־# Netscape HTTP Cookie File ולכלול עוגיות
					של youtube.com.
				</p>
				<p v-if="selectedCookiesFile" class="selected-cookie-file">
					נבחר: {{ selectedCookiesOriginalName }}
				</p>
				<div class="cookies-file-actions">
					<button type="button" class="cookies-action-button primary"
						:disabled="!selectedCookiesFile || isLoading || isCookiesUploading"
						@click="handleManualCookiesUpload()">
						{{ isCookiesUploading ? 'שולח עוגיות...' : 'שלח קובץ עוגיות' }}
					</button>
					<button v-if="selectedCookiesFile" type="button" class="cookies-action-button secondary"
						:disabled="isLoading || isCookiesUploading" @click="clearSelectedCookiesFile">
						הסר בחירה והשתמש בסנכרון אוטומטי
					</button>
				</div>
				<p v-if="cookiesUploadStatus" class="cookies-upload-status" :class="cookiesUploadState"
					role="status" aria-live="polite">
					{{ cookiesUploadStatus }}
				</p>

				<details class="cookies-help">
					<summary>איך מפיקים cookies.txt תקין ובטוח?</summary>
					<div class="cookies-help-content">
						<p>
							התוסף המומלץ ל־Chrome הוא
							<a href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
								target="_blank" rel="noopener noreferrer">Get cookies.txt LOCALLY</a>.
							ודא שזה השם המדויק, כולל המילה <strong>LOCALLY</strong>; אל תתקין תוסף בעל שם דומה בלי מילה זו.
						</p>
						<ol>
							<li>התקן את התוסף מהקישור הרשמי של Chrome Web Store.</li>
							<li>פתח <code>chrome://extensions</code>, היכנס לפרטי התוסף ואפשר לו לפעול בחלון גלישה בסתר.</li>
							<li>סגור חלונות גלישה בסתר אחרים, פתח חלון חדש והשאר בו לשונית אחת בלבד. מומלץ להשתמש בחשבון YouTube נפרד שמיועד להורדות.</li>
							<li>התחבר ל־YouTube, ובאותה לשונית עבור לכתובת <code>https://www.youtube.com/robots.txt</code>.</li>
							<li>פתח את Get cookies.txt LOCALLY, בחר ייצוא בפורמט Netscape ושמור את עוגיות youtube.com.</li>
							<li>סגור מיד את חלון הגלישה בסתר ואל תפתח שוב את אותה הפעלה, כדי לצמצם סיבוב של העוגיות.</li>
							<li>בחר כאן את הקובץ ולחץ "שלח קובץ עוגיות", או לחץ על כפתור ההעלאה הראשי והקובץ יישלח תחילה אוטומטית.</li>
						</ol>
						<p class="cookies-security-warning">
							קובץ cookies.txt הוא מידע התחברות רגיש. אין לשלוח אותו במייל, לשתף אותו עם אדם אחר או
							להעלות אותו לשירות שאינך סומך עליו. שימוש בעוגיות חשבון עלול גם לחשוף את החשבון להגבלות
							של YouTube; השתמש בהן רק כשנדרש.
						</p>
						<p>
							<a href="https://github.com/yt-dlp/yt-dlp/wiki/Extractors#exporting-youtube-cookies"
								target="_blank" rel="noopener noreferrer">פתח את מדריך ייצוא העוגיות הרשמי של yt-dlp</a>
						</p>
					</div>
				</details>
			</div>

			<netfree-checker v-if="showChecker" ref="netfreeChecker" :url="videoUrl" :checking="isChecking"
				:check-status="checkStatus" :check-message="checkMessage" />

			<button type="submit" class="upload-btn"
				:disabled="isLoading || isCookiesUploading || (showChecker && !isAccessible)">
				{{ getSubmitButtonLabel() }}
			</button>
		</form>

		<div v-if="isLoading" class="upload-status" ref="uploadStatus">
			<div class="loader"></div>
			<div class="status-info">
				<h3>{{ getStatusTitle() }}</h3>
				<p>{{ uploadStatus }}</p>
				<div class="progress-bar">
					<div class="progress" :style="{ width: `${uploadProgress}%` }"></div>
				</div>
				<p>{{ uploadProgress }}%</p>
			</div>
		</div>

		<div v-if="uploadResult && uploadComplete" class="upload-result" ref="uploadResult"
			:class="{ 'error-result': hasFatalError }">
			<div v-if="hasFatalError" class="error-message">
				{{ uploadResult }}
			</div>
			<div v-else class="success-message">
				{{ getCompletionHeadline() }}
			</div>
			<p v-if="getResultDetail()" class="result-detail">{{ getResultDetail() }}</p>
			<p v-if="uploadErrorDetails" class="result-note">{{ uploadErrorDetails }}</p>
			<div v-if="driveLink" class="drive-link">
				<a :href="driveLink" target="_blank" rel="noopener noreferrer">{{ getDriveLinkLabel() }}</a>
			</div>
		</div>
	</div>
</template>

<script>
import ApiService from '@/services/api';
import AuthService from '@/services/auth';
import NetfreeChecker from '@/components/NetfreeChecker.vue';

const REQUIRED_EXTENSION_CAPABILITY = 'youtube-cookie-sync-v1';
const COOKIE_SYNC_TIMEOUT_MS = 20000;
const MAX_COOKIE_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export default {
	name: 'VideoUploader',
	components: {
		NetfreeChecker
	}, data() {
		return {
			contentModes: [
				{
					value: 'auto',
					label: 'זיהוי אוטומטי',
					description: 'המערכת תזהה לבד אם זה סרטון, פלייליסט או ערוץ.'
				},
				{
					value: 'playlist',
					label: 'פלייליסט',
					description: 'שולח ישירות למסלול הפלייליסט ויוצר תיקייה ב-Drive.'
				},
				{
					value: 'channel',
					label: 'ערוץ',
					description: 'שולח ישירות למסלול הערוץ ומעלה את סרטוני הערוץ לתיקייה.'
				}
			],
			selectedContentMode: 'auto',
			videoUrl: '',
			formatType: 'video',
			qualityPreference: 'best',
			isLoading: false,
			showChecker: false,
			isChecking: false,
			checkStatus: null,
			checkMessage: '',
			isAccessible: false,
			uploadResult: null,
			taskId: null,
			uploadStatus: '',
			uploadProgress: 0,
			uploadComplete: false,
			driveLink: '',
			detectedContentKind: null,
			uploadErrorDetails: '',
			hasFatalError: false,
			statusCheckInterval: null,
			isGapiLoaded: false,
			isCookiesUploading: false,
			cookiesUploadStatus: '',
			cookiesUploadState: '',
			selectedCookiesFile: null,
			selectedCookiesOriginalName: '',
		};
	},
	mounted() {
		// בדיקת נוכחות תוסף בטעינת הקומפוננטה
		this.checkIfExtensionInstalled().then(isExtensionAvailable => {
			if (!isExtensionAvailable) {
				console.log('Extension usage enforced - redirecting to install page');
				this.$router.push('/install-extension');
				return;
			}
		});

		this.loadGoogleApi();

		const pendingVideoUrl = localStorage.getItem('pendingVideoUrl');
		if (pendingVideoUrl) {
			this.videoUrl = pendingVideoUrl;

			const pendingFormatType = localStorage.getItem('pendingFormatType');
			if (pendingFormatType) {
				this.formatType = pendingFormatType;
			}

			const pendingQualityPreference = localStorage.getItem('pendingQualityPreference');
			if (pendingQualityPreference) {
				this.qualityPreference = pendingQualityPreference;
			}

			const pendingContentKind = localStorage.getItem('pendingContentKind');
			if (pendingContentKind) {
				this.selectedContentMode = pendingContentKind;
			}

			localStorage.removeItem('pendingVideoUrl');
			localStorage.removeItem('pendingFormatType');
			localStorage.removeItem('pendingQualityPreference');
			localStorage.removeItem('pendingContentKind');

			this.$nextTick(() => {
				this.handleSubmit();
			});
		}
	},
	beforeUnmount() {
		this.clearStatusInterval();
	},
	methods: {
		async handleCookiesSelection(event) {
			const input = event.target;
			const file = input.files && input.files[0];
			if (!file) {
				this.clearSelectedCookiesFile();
				return;
			}

			this.selectedCookiesFile = null;
			this.selectedCookiesOriginalName = '';
			this.cookiesUploadState = 'uploading';
			this.cookiesUploadStatus = 'בודק את קובץ העוגיות...';

			try {
				if (file.size > MAX_COOKIE_FILE_SIZE_BYTES) {
					throw new Error('קובץ העוגיות גדול מדי. הגודל המרבי הוא 5MB.');
				}

				const normalizedContents = (await file.text())
					.replace(/^\uFEFF/, '')
					.replace(/\r\n?/g, '\n');
				if (!normalizedContents.startsWith('# Netscape HTTP Cookie File')) {
					throw new Error('הקובץ אינו בפורמט Netscape תקין. השורה הראשונה חייבת להיות # Netscape HTTP Cookie File.');
				}

				const youtubeCookiePattern = /^(?:#HttpOnly_)?\.?(?:[a-z0-9-]+\.)*youtube\.com\t/im;
				if (!youtubeCookiePattern.test(normalizedContents)) {
					throw new Error('לא נמצאו בקובץ עוגיות השייכות ל־youtube.com.');
				}

				const contentsWithTrailingNewline = normalizedContents.endsWith('\n')
					? normalizedContents
					: `${normalizedContents}\n`;
				this.selectedCookiesFile = new File(
					[contentsWithTrailingNewline],
					'cookies.txt',
					{ type: 'text/plain;charset=utf-8' }
				);
				this.selectedCookiesOriginalName = file.name;
				this.cookiesUploadState = 'ready';
				this.cookiesUploadStatus = 'הקובץ תקין ומוכן לשליחה. אפשר לשלוח אותו עכשיו או יחד עם בקשת ההורדה.';
			} catch (error) {
				this.cookiesUploadState = 'error';
				this.cookiesUploadStatus = error.message || 'בדיקת קובץ העוגיות נכשלה. אנא נסה שוב.';
				input.value = '';
			}
		},
		clearSelectedCookiesFile() {
			this.selectedCookiesFile = null;
			this.selectedCookiesOriginalName = '';
			this.cookiesUploadStatus = '';
			this.cookiesUploadState = '';

			if (this.$refs.youtubeCookiesInput) {
				this.$refs.youtubeCookiesInput.value = '';
			}
		},
		async uploadSelectedCookiesFile() {
			if (!this.selectedCookiesFile) {
				throw new Error('לא נבחר קובץ עוגיות תקין.');
			}

			this.isCookiesUploading = true;
			this.cookiesUploadState = 'uploading';
			this.cookiesUploadStatus = 'שולח את קובץ העוגיות לשירות ההורדות...';

			try {
				const result = await ApiService.uploadYouTubeCookies(this.selectedCookiesFile);
				this.cookiesUploadState = 'success';
				this.cookiesUploadStatus = result.message || 'קובץ העוגיות נשלח בהצלחה.';
				return result;
			} catch (error) {
				this.cookiesUploadState = 'error';
				this.cookiesUploadStatus = error.message || 'שליחת קובץ העוגיות נכשלה. אנא נסה שוב.';
				throw error;
			} finally {
				this.isCookiesUploading = false;
			}
		},
		async handleManualCookiesUpload() {
			try {
				await this.uploadSelectedCookiesFile();
			} catch (error) {
				// מצב השגיאה כבר מוצג ליד שדה הקובץ.
			}
		},
		async prepareYouTubeCookiesForDownload() {
			if (!this.selectedCookiesFile) {
				return this.syncLatestYouTubeCookiesFromExtension();
			}

			this.uploadStatus = 'שולח את קובץ עוגיות YouTube שנבחר לפני בקשת ההורדה...';
			try {
				return await this.uploadSelectedCookiesFile();
			} catch (error) {
				const preparationError = new Error(
					this.cookiesUploadStatus || 'לא ניתן לשלוח את קובץ עוגיות YouTube. ההורדה לא נשלחה.'
				);
				preparationError.code = 'COOKIE_SYNC_REQUIRED';
				throw preparationError;
			}
		},
		createExtensionRequestId() {
			if (window.crypto && typeof window.crypto.randomUUID === 'function') {
				return window.crypto.randomUUID();
			}

			return `youtube-cookie-sync-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		},
		requestYouTubeCookieSync() {
			return new Promise((resolve, reject) => {
				const requestId = this.createExtensionRequestId();
				let timeoutId = null;

				const cleanup = () => {
					window.removeEventListener('message', resultListener);
					if (timeoutId) {
						clearTimeout(timeoutId);
					}
				};

				const resultListener = (event) => {
					if (event.source !== window || event.origin !== window.location.origin ||
						!event.data || event.data.type !== 'NETFREE_SYNC_YOUTUBE_COOKIES_RESULT' ||
						event.data.requestId !== requestId) {
						return;
					}

					cleanup();
					if (!event.data.ok) {
						const error = new Error(event.data.error || 'YouTube cookie sync failed.');
						error.code = event.data.errorCode || 'cookie_sync_failed';
						reject(error);
						return;
					}

					resolve(event.data);
				};

				window.addEventListener('message', resultListener);
				timeoutId = setTimeout(() => {
					cleanup();
					const error = new Error('The extension did not answer in time.');
					error.code = 'cookie_sync_response_timeout';
					reject(error);
				}, COOKIE_SYNC_TIMEOUT_MS);

				window.postMessage({
					type: 'NETFREE_SYNC_YOUTUBE_COOKIES',
					requestId
				}, window.location.origin);
			});
		},
		getCookieSyncErrorMessage(error) {
			const messages = {
				no_youtube_cookies: 'לא נמצאו בדפדפן עוגיות YouTube תקינות. פתח את youtube.com, התחבר או אשר עוגיות, ונסה שוב.',
				cookie_read_failed: 'התוסף לא הצליח לקרוא את עוגיות YouTube. ודא שאישרת לתוסף את הרשאת העוגיות וטען אותו מחדש.',
				cookie_upload_timeout: 'העלאת עוגיות YouTube לשירות ההורדות ארכה זמן רב מדי. נסה שוב.',
				cookie_upload_unreachable: 'לא ניתן להתחבר לשירות ההורדות לצורך העלאת עוגיות YouTube.',
				cookie_upload_failed: 'שירות ההורדות דחה את קובץ עוגיות YouTube.',
				unauthorized_origin: 'גרסת התוסף אינה מורשית לפעול מכתובת האתר הנוכחית.',
				extension_runtime_error: 'התוסף לא הגיב כראוי. הורד את הגרסה החדשה, טען אותה מחדש ונסה שוב.',
				cookie_sync_response_timeout: 'לא התקבלה תשובה מהתוסף. הורד את הגרסה החדשה, טען אותה מחדש ונסה שוב.',
				invalid_cookie_data: 'עוגיות YouTube שנמצאו אינן ניתנות להמרה לקובץ תקין.'
			};

			return messages[error.code] || 'לא ניתן להכין את עוגיות YouTube העדכניות. ההורדה לא נשלחה.';
		},
		async syncLatestYouTubeCookiesFromExtension() {
			this.isCookiesUploading = true;
			this.cookiesUploadState = 'uploading';
			this.cookiesUploadStatus = 'מקבל מהתוסף את עוגיות YouTube העדכניות ומעלה אותן...';
			this.uploadStatus = 'מכין עוגיות YouTube עדכניות לפני שליחת ההורדה...';

			try {
				const result = await this.requestYouTubeCookieSync();
				if (!Number.isFinite(result.count) || result.count < 1) {
					const error = new Error('No YouTube cookies were synced.');
					error.code = 'no_youtube_cookies';
					throw error;
				}

				this.cookiesUploadState = 'success';
				this.cookiesUploadStatus = `סונכרנו בהצלחה ${result.count} עוגיות YouTube עדכניות.`;
				return result;
			} catch (error) {
				const message = this.getCookieSyncErrorMessage(error);
				this.cookiesUploadState = 'error';
				this.cookiesUploadStatus = message;

				const syncError = new Error(message);
				syncError.code = 'COOKIE_SYNC_REQUIRED';
				throw syncError;
			} finally {
				this.isCookiesUploading = false;
			}
		},
		async loadGoogleApi() {
			if (window.gapi) {
				console.log('gapi already loaded');

				return new Promise((resolve) => {
					if (!window.gapi.auth) {
						console.log('Loading auth module...');
						window.gapi.load('auth', () => {
							console.log('Auth module loaded');
							this.isGapiLoaded = true;
							resolve();
						});
					} else {
						this.isGapiLoaded = true;
						resolve();
					}
				});
			}

			console.log('Loading Google API...');

			return new Promise((resolve, reject) => {
				window.onGoogleApiLoad = () => {
					console.log('Google API loaded successfully');
					window.gapi.load('auth', () => {
						console.log('Auth module loaded');
						this.isGapiLoaded = true;
						resolve();
					});
				};

				const script = document.createElement('script');
				script.src = 'https://apis.google.com/js/api.js?onload=onGoogleApiLoad';
				script.onerror = (error) => {
					console.error('Google API failed to load', error);
					reject(error);
				};
				document.head.appendChild(script);
			});
		},
		getContentMeta(kind = this.getEffectiveContentKind()) {
			const meta = {
				generic: {
					name: 'תוכן',
					urlLabel: 'קישור ליוטיוב:',
					placeholder: 'הזן קישור לסרטון, פלייליסט או ערוץ מיוטיוב',
					modeHint: 'המערכת תזהה לבד אם הקישור הוא סרטון, פלייליסט או ערוץ.',
					successHeadline: 'התוכן הועלה בהצלחה!',
					statusTitle: 'סטטוס העלאה',
					driveLinkLabel: 'פתח בגוגל דרייב',
					netfreeLabel: 'התוכן',
					submitLabel: 'העלה לדרייב'
				},
				single: {
					name: 'סרטון',
					urlLabel: 'קישור לסרטון ביוטיוב:',
					placeholder: 'הזן את כתובת הסרטון מיוטיוב',
					modeHint: 'המסלול הרגיל יזהה סרטון בודד ויעלה קובץ אחד ל-Drive.',
					successHeadline: 'הסרטון הועלה בהצלחה!',
					statusTitle: 'סטטוס העלאת סרטון',
					driveLinkLabel: 'פתח את הקובץ בגוגל דרייב',
					netfreeLabel: 'הסרטון',
					submitLabel: 'העלה סרטון לדרייב'
				},
				playlist: {
					name: 'פלייליסט',
					urlLabel: 'קישור לפלייליסט ביוטיוב:',
					placeholder: 'הזן קישור לפלייליסט, למשל https://www.youtube.com/playlist?list=...',
					modeHint: 'הפלייליסט יישלח ישירות למסלול האוסף וייווצר עבורו תיקייה חדשה ב-Drive.',
					successHeadline: 'הפלייליסט הועלה בהצלחה!',
					statusTitle: 'סטטוס העלאת פלייליסט',
					driveLinkLabel: 'פתח את התיקייה בגוגל דרייב',
					netfreeLabel: 'הפלייליסט',
					submitLabel: 'העלה פלייליסט לדרייב'
				},
				channel: {
					name: 'ערוץ',
					urlLabel: 'קישור לערוץ יוטיוב:',
					placeholder: 'הזן קישור לערוץ, למשל https://www.youtube.com/@channel/videos',
					modeHint: 'הערוץ יישלח ישירות למסלול הערוץ והקבצים יועלו לתיקייה חדשה ב-Drive.',
					successHeadline: 'הערוץ הועלה בהצלחה!',
					statusTitle: 'סטטוס העלאת ערוץ',
					driveLinkLabel: 'פתח את התיקייה בגוגל דרייב',
					netfreeLabel: 'הערוץ',
					submitLabel: 'העלה ערוץ לדרייב'
				}
			};

			return meta[kind] || meta.generic;
		},
		getEffectiveContentKind() {
			if (this.selectedContentMode === 'auto') {
				return this.detectedContentKind || 'generic';
			}

			return this.selectedContentMode;
		},
		getModeHint() {
			if (this.selectedContentMode === 'auto') {
				return this.getContentMeta('generic').modeHint;
			}

			return this.getContentMeta(this.selectedContentMode).modeHint;
		},
		getUrlLabel() {
			if (this.selectedContentMode === 'auto') {
				return this.getContentMeta('generic').urlLabel;
			}

			return this.getContentMeta(this.selectedContentMode).urlLabel;
		},
		getUrlPlaceholder() {
			if (this.selectedContentMode === 'auto') {
				return this.getContentMeta('generic').placeholder;
			}

			return this.getContentMeta(this.selectedContentMode).placeholder;
		},
		getSubmitButtonLabel() {
			if (this.selectedContentMode === 'auto') {
				return this.getContentMeta('generic').submitLabel;
			}

			return this.getContentMeta(this.selectedContentMode).submitLabel;
		},
		getStatusTitle() {
			return this.getContentMeta().statusTitle;
		},
		getDriveLinkLabel() {
			return this.getContentMeta().driveLinkLabel;
		},
		getCompletionHeadline() {
			if (this.uploadResult && this.uploadResult.includes('חלקית')) {
				return this.uploadResult;
			}

			return this.getContentMeta().successHeadline;
		},
		getResultDetail() {
			if (!this.uploadResult) {
				return '';
			}

			const completionHeadline = this.getCompletionHeadline();
			return this.uploadResult === completionHeadline ? '' : this.uploadResult;
		},
		getInitialUploadStatus() {
			if (this.selectedContentMode === 'auto') {
				return 'מתחיל תהליך העלאה וזיהוי סוג התוכן...';
			}

			return `מתחיל תהליך העלאת ${this.getContentMeta(this.selectedContentMode).name}...`;
		},
		normalizePlaylistUrl(url) {
			try {
				const parsedUrl = new URL(url.trim());
				const isYoutubeHost = parsedUrl.hostname.includes('youtube.com') || parsedUrl.hostname === 'youtu.be';
				if (!isYoutubeHost) {
					return null;
				}

				const playlistId = parsedUrl.searchParams.get('list');
				if (!playlistId) {
					return null;
				}

				return `https://www.youtube.com/playlist?list=${encodeURIComponent(playlistId)}`;
			} catch (error) {
				return null;
			}
		},
		extractChannelBasePath(pathname) {
			const segments = pathname.split('/').filter(Boolean);
			if (!segments.length) {
				return null;
			}

			const [firstSegment, secondSegment] = segments;
			if (firstSegment.startsWith('@')) {
				return `/${firstSegment}`;
			}

			if (['channel', 'c', 'user'].includes(firstSegment) && secondSegment) {
				return `/${firstSegment}/${secondSegment}`;
			}

			return null;
		},
		normalizeChannelUrl(url) {
			try {
				const parsedUrl = new URL(url.trim());
				if (!parsedUrl.hostname.includes('youtube.com')) {
					return null;
				}

				const channelBasePath = this.extractChannelBasePath(parsedUrl.pathname);
				if (!channelBasePath) {
					return null;
				}

				return `https://www.youtube.com${channelBasePath}/videos`;
			} catch (error) {
				return null;
			}
		},
		validateAndNormalizeSelectedUrl() {
			const trimmedUrl = this.videoUrl.trim();
			if (!trimmedUrl) {
				return {
					valid: false,
					message: 'אנא הזן קישור תקין ליוטיוב'
				};
			}

			if (this.selectedContentMode === 'playlist') {
				const normalizedUrl = this.normalizePlaylistUrl(trimmedUrl);
				if (!normalizedUrl) {
					return {
						valid: false,
						message: 'במצב פלייליסט יש להזין קישור שמכיל list= או קישור playlist מלא.'
					};
				}

				return {
					valid: true,
					normalizedUrl
				};
			}

			if (this.selectedContentMode === 'channel') {
				const normalizedUrl = this.normalizeChannelUrl(trimmedUrl);
				if (!normalizedUrl) {
					return {
						valid: false,
						message: 'במצב ערוץ יש להזין קישור ערוץ תקין כמו youtube.com/@name או youtube.com/channel/....'
					};
				}

				return {
					valid: true,
					normalizedUrl
				};
			}

			return {
				valid: true,
				normalizedUrl: trimmedUrl
			};
		},
		getStartedUploadStatus() {
			const effectiveContentKind = this.getEffectiveContentKind();

			switch (effectiveContentKind) {
				case 'single':
					return this.selectedContentMode === 'auto'
						? 'זוהה סרטון, ההעלאה החלה ומתקבלים עדכוני סטטוס...'
						: 'העלאת הסרטון החלה, מתקבלים עדכוני סטטוס...';
				case 'playlist':
					return this.selectedContentMode === 'auto'
						? 'זוהה פלייליסט, ההעלאה החלה ומתקבלים עדכוני סטטוס...'
						: 'העלאת הפלייליסט החלה, מתקבלים עדכוני סטטוס...';
				case 'channel':
					return this.selectedContentMode === 'auto'
						? 'זוהה ערוץ, ההעלאה החלה ומתקבלים עדכוני סטטוס...'
						: 'העלאת הערוץ החלה, מתקבלים עדכוני סטטוס...';
				default:
					return 'ההעלאה החלה, מתקבלים עדכוני סטטוס...';
			}
		},
		setContentMode(mode) {
			if (this.selectedContentMode === mode) {
				return;
			}

			this.selectedContentMode = mode;
			this.resetCheckStatus();
		},
		clearStatusInterval() {
			if (this.statusCheckInterval) {
				clearInterval(this.statusCheckInterval);
				this.statusCheckInterval = null;
			}
		},
		finalizeSuccessfulUpload(statusData) {
			this.uploadStatus = statusData.status || this.uploadStatus;
			this.uploadProgress = statusData.progress || 100;
			this.driveLink = statusData.link || '';
			this.uploadResult = statusData.status || this.getCompletionHeadline();
			this.uploadErrorDetails = statusData.error || '';
			this.uploadComplete = true;
			this.hasFatalError = false;
			this.isLoading = false;
			this.videoUrl = '';
			this.clearStatusInterval();
			this.scrollToElement('uploadResult');
		},
		async checkNetFreeAvailability() {
			this.showChecker = true;
			this.isChecking = true;
			this.checkStatus = null;
			this.checkMessage = 'בודק זמינות באמצעות תוסף הדפדפן...';

			const netfreeTarget = this.selectedContentMode === 'auto'
				? this.getContentMeta('generic').netfreeLabel
				: this.getContentMeta(this.selectedContentMode).netfreeLabel;

			try {
				// שלב 1: בדיקת תוסף כבר בוצעה בטעינת הקומפוננטה
				// כעת אנחנו פשוט משתמשים בו
				const statusCode = await this.checkUrlWithExtension(this.videoUrl);

				this.isChecking = false;

				// לוגיקת הסטטוסים
				if (statusCode === 200) {
					this.checkStatus = 'accessible';
					this.checkMessage = `${netfreeTarget} זמין וניתן להורדה בנטפרי`;
					this.isAccessible = true;
				}
				else if (statusCode === 418) {
					this.checkStatus = 'accessible';
					this.checkMessage = `${netfreeTarget} לא זמין בנטפרי אבל באופן זמני אני מאשר לאשר...`;
					this.isAccessible = true;
					// this.checkStatus = 'blocked';
					// this.checkMessage = `${netfreeTarget} חסום בנטפרי (418) ולא ניתן להורדה.`;
					// this.isAccessible = false;
				}
				else if (statusCode === 'error') {
					throw new Error('Check failed');
				}
				else {
					this.checkStatus = 'blocked';
					this.checkMessage = `${netfreeTarget} לא זמין או חסום (סטטוס: ${statusCode}).`;
					this.isAccessible = false;
				}

				this.scrollToElement('netfreeChecker');

			} catch (error) {
				console.error('Error checking video:', error);
				this.isChecking = false;
				this.checkStatus = 'error';
				this.checkMessage = `לא ניתן לבדוק את זמינות ${netfreeTarget}. התוסף לא מחזיר תשובה.`;
				this.isAccessible = false;
				// במקרה של שגיאה עם התוסף, נפנה לדף ההתקנה שוב אם רוצים, או שנציג שגיאה
			}

			return this.isAccessible;
		},
		// helpers for extension communication
		checkIfExtensionInstalled() {
			return new Promise((resolve) => {
				let attempts = 0;
				const maxAttempts = 20; // Try for ~4 seconds (20 * 200ms)

				const pongListener = (event) => {
					if (event.source === window && event.origin === window.location.origin &&
						event.data && event.data.type === 'CHECK_NETFREE_PONG') {
						clearInterval(pingInterval);
						window.removeEventListener('message', pongListener);
						const capabilities = Array.isArray(event.data.capabilities) ? event.data.capabilities : [];
						resolve(capabilities.includes(REQUIRED_EXTENSION_CAPABILITY));
					}
				};

				window.addEventListener('message', pongListener);

				// Function to send ping
				const sendPing = () => window.postMessage(
					{ type: 'CHECK_NETFREE_PING' },
					window.location.origin
				);

				sendPing(); // First attempt

				const pingInterval = setInterval(() => {
					attempts++;
					if (attempts >= maxAttempts) {
						clearInterval(pingInterval);
						window.removeEventListener('message', pongListener);
						resolve(false);
					} else {
						sendPing();
					}
				}, 200);
			});
		},
		checkUrlWithExtension(url) {
			return new Promise((resolve) => {
				let timeoutId = null;
				const cleanup = () => {
					window.removeEventListener('message', resultListener);
					if (timeoutId) {
						clearTimeout(timeoutId);
					}
				};

				const resultListener = (event) => {
					if (event.source === window && event.origin === window.location.origin && event.data &&
						event.data.type === 'CHECK_NETFREE_RESULT' &&
						event.data.url === url) {

						cleanup();
						resolve(event.data.status);
					}
				};

				window.addEventListener('message', resultListener);
				window.postMessage({ type: 'CHECK_NETFREE_URL', url: url }, window.location.origin);

				// Timeout of 10 seconds just in case
				timeoutId = setTimeout(() => {
					cleanup();
					resolve('error');
				}, 10000);
			});
		},
		async handleSubmit() {
			const validationResult = this.validateAndNormalizeSelectedUrl();
			if (!validationResult.valid) {
				alert(validationResult.message);
				this.uploadResult = validationResult.message;
				this.uploadComplete = true;
				this.hasFatalError = true;
				this.driveLink = '';
				this.uploadErrorDetails = '';
				return;
			}

			this.videoUrl = validationResult.normalizedUrl;
			this.clearStatusInterval();
			this.isLoading = true;
			this.uploadResult = null;
			this.uploadStatus = this.getInitialUploadStatus();
			this.uploadProgress = 0;
			this.uploadComplete = false;
			this.driveLink = '';
			this.uploadErrorDetails = '';
			this.hasFatalError = false;
			this.detectedContentKind = this.selectedContentMode === 'auto' ? null : this.selectedContentMode;

			if (!this.showChecker) {
				const isAccessible = await this.checkNetFreeAvailability();
				if (!isAccessible) {
					this.isLoading = false;
					this.videoUrl = '';
					return;
				}
			}
			try {
				const tokens = JSON.parse(AuthService.getToken());
				if (!tokens.refresh_token) {
					this.uploadStatus = 'חסר token לגישה ממושכת. מבצע התחברות מחדש...';

					localStorage.setItem('pendingVideoUrl', this.videoUrl);
					localStorage.setItem('pendingFormatType', this.formatType);
					localStorage.setItem('pendingQualityPreference', this.qualityPreference);
					localStorage.setItem('pendingContentKind', this.selectedContentMode);

					window.location.href = ApiService.getAuthUrl();
					return;
				}

				this.scrollToElement('uploadStatus');
				await this.prepareYouTubeCookiesForDownload();

				const response = await ApiService.uploadVideo(
					this.videoUrl,
					tokens,
					null,
					this.formatType,
					this.qualityPreference,
					this.selectedContentMode
				);
				this.taskId = response.data.task_id;
				this.detectedContentKind = response.data.content_kind || this.detectedContentKind || 'single';
				this.uploadStatus = this.getStartedUploadStatus();

				this.scrollToElement('uploadStatus');

				this.startStatusCheck();
			} catch (error) {
				console.error('Upload error:', error);
				this.uploadStatus = error.code === 'COOKIE_SYNC_REQUIRED'
					? error.message
					: `שגיאה בהעלאת ${this.getContentMeta().name}. אנא נסה שנית.`;
				this.uploadResult = this.uploadStatus;
				this.uploadComplete = true;
				this.hasFatalError = true;
				this.isLoading = false;
			}
		},

		startStatusCheck() {
			this.clearStatusInterval();
			this.statusCheckInterval = setInterval(async () => {
				if (!this.taskId) {
					this.clearStatusInterval();
					return;
				}

				try {
					const statusResponse = await ApiService.checkTaskStatus(this.taskId);
					const statusData = statusResponse.data;

					this.uploadStatus = statusData.status || this.uploadStatus;
					this.uploadProgress = statusData.progress || 0;

					if (statusData.link) {
						this.finalizeSuccessfulUpload(statusData);
						return;
					}

					if (statusData.error) {
						this.uploadStatus = `שגיאה: ${statusData.error}`;
						this.isLoading = false;
						this.uploadComplete = true;
						this.uploadResult = `שגיאה: ${statusData.error}`;
						this.uploadErrorDetails = '';
						this.hasFatalError = true;
						this.clearStatusInterval();
						this.scrollToElement('uploadStatus');
						return;
					}
				} catch (error) {
					console.error('Error checking status:', error);
					this.uploadStatus = 'שגיאה בבדיקת סטטוס המשימה';
					this.uploadResult = 'שגיאה בבדיקת סטטוס המשימה';
					this.isLoading = false;
					this.uploadComplete = true;
					this.uploadErrorDetails = '';
					this.hasFatalError = true;
					this.clearStatusInterval();
				}
			}, 1000);
		},
		resetCheckStatus() {
			console.log('Resetting check status due to URL change');
			this.showChecker = false;
			this.isAccessible = false;
			this.checkStatus = null;
			this.checkMessage = '';
			this.uploadResult = null;
			this.uploadErrorDetails = '';
			this.uploadComplete = false;
			this.driveLink = '';
			this.hasFatalError = false;
			this.detectedContentKind = this.selectedContentMode === 'auto' ? null : this.selectedContentMode;
		},
		scrollToElement(refName) {
			this.$nextTick(() => {
				const refElement = this.$refs[refName];
				if (refElement) {
					if (refElement.$el) {
						refElement.$el.scrollIntoView({
							behavior: 'smooth',
							block: 'start'
						});
					} else if (refElement instanceof HTMLElement) {
						refElement.scrollIntoView({
							behavior: 'smooth',
							block: 'start'
						});
					} else if (Array.isArray(refElement) && refElement.length > 0) {
						const firstElement = refElement[0].$el || refElement[0];
						if (firstElement && firstElement.scrollIntoView) {
							firstElement.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
						}
					}
				}
			});
		}
	}
}
</script>

<style scoped>
.uploader-container {
	position: relative;
}

.form-group {
	margin-bottom: 20px;
}

.content-mode-selector {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
}

.mode-button {
	display: flex;
	flex-direction: column;
	gap: 6px;
	align-items: flex-start;
	text-align: right;
	border: 2px solid #dfe1e5;
	border-radius: 10px;
	background: #fff;
	padding: 14px 16px;
	cursor: pointer;
	transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
	color: var(--text-color);
}

.mode-button:hover {
	border-color: var(--primary-color);
	transform: translateY(-1px);
}

.mode-button:disabled {
	cursor: not-allowed;
	opacity: 0.7;
	transform: none;
}

.mode-button.active {
	border-color: var(--primary-color);
	box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.15);
}

.mode-button span {
	font-size: 14px;
	line-height: 1.4;
}

.field-hint {
	margin: 10px 0 0;
	color: #5f6368;
	font-size: 14px;
}

.cookies-upload-section {
	margin-bottom: 20px;
	padding: 16px;
	border: 1px solid #dfe1e5;
	border-radius: 8px;
	background: #fff;
}

.cookie-sync-notice {
	margin: 0 0 14px;
	padding: 12px;
	border-right: 4px solid var(--primary-color);
	background: #eef4ff;
	color: #334155;
	line-height: 1.6;
}

.selected-cookie-file {
	margin: 10px 0 0;
	font-weight: 600;
	word-break: break-word;
}

.cookies-file-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 12px;
}

.cookies-action-button {
	border: 0;
	border-radius: 6px;
	padding: 9px 14px;
	cursor: pointer;
	font: inherit;
}

.cookies-action-button.primary {
	background: var(--primary-color);
	color: #fff;
}

.cookies-action-button.secondary {
	background: #e8eaed;
	color: var(--text-color);
}

.cookies-action-button:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.cookies-help {
	margin-top: 18px;
	border-top: 1px solid #dfe1e5;
	padding-top: 14px;
}

.cookies-help summary {
	cursor: pointer;
	font-weight: 600;
	color: var(--primary-color);
}

.cookies-help-content {
	margin-top: 12px;
	line-height: 1.65;
}

.cookies-help-content ol {
	padding-right: 22px;
}

.cookies-help-content li {
	margin-bottom: 8px;
}

.cookies-help-content a {
	color: var(--primary-color);
}

.cookies-security-warning {
	padding: 12px;
	border-right: 4px solid var(--warning-color);
	background: #fff8e1;
}

.cookies-file-input {
	display: block;
	width: 100%;
	box-sizing: border-box;
}

.cookies-upload-status {
	margin: 12px 0 0;
	font-weight: 500;
}

.cookies-upload-status.uploading {
	color: #5f6368;
}

.cookies-upload-status.ready {
	color: var(--primary-color);
}

.cookies-upload-status.success {
	color: #0d652d;
}

.cookies-upload-status.error {
	color: var(--danger-color);
}

label {
	display: block;
	margin-bottom: 8px;
	font-weight: 500;
}

input[type="text"] {
	width: 100%;
	padding: 12px 15px;
	margin-bottom: 20px;
	border: 2px solid #dfe1e5;
	border-radius: 8px;
	font-size: 16px;
	transition: border-color 0.3s;
	box-sizing: border-box;
}

input[type="text"]:focus {
	outline: none;
	border-color: var(--primary-color);
}

.upload-btn {
	background-color: var(--secondary-color);
	color: white;
	border: none;
	padding: 12px 24px;
	border-radius: 6px;
	font-size: 16px;
	cursor: pointer;
	transition: background-color 0.3s, transform 0.2s;
	display: block;
	margin: 0 auto;
}

.upload-btn:hover:not(:disabled) {
	background-color: #2d9249;
	transform: translateY(-2px);
}

.upload-btn:disabled {
	background-color: #cccccc;
	cursor: not-allowed;
}

.loader {
	border: 4px solid #f3f3f3;
	border-top: 4px solid var(--primary-color);
	border-radius: 50%;
	width: 30px;
	height: 30px;
	animation: spin 1s linear infinite;
	margin: 20px auto;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}

.upload-result {
	margin-top: 20px;
	padding: 15px;
	background-color: #e6f4ea;
	border-radius: 8px;
	text-align: center;
}

.success-message {
	color: #0d652d;
	font-weight: 500;
}

.result-detail,
.result-note {
	margin-top: 10px;
}

.result-note {
	color: #8a4b00;
}

.upload-status {
	margin-top: 20px;
	border: 1px solid #ddd;
	padding: 15px;
	border-radius: 5px;
	background: #f9f9f9;
}

.progress-bar {
	width: 100%;
	height: 20px;
	background: #eee;
	border-radius: 10px;
	margin: 10px 0;
	overflow: hidden;
}

.progress {
	height: 100%;
	background: #4caf50;
	border-radius: 10px;
	transition: width 0.3s ease;
}

.form-options {
	display: flex;
	justify-content: space-between;
	margin-bottom: 20px;
}

.format-group,
.quality-group {
	flex: 1;
	margin-right: 10px;
}

.quality-group {
	margin-right: 0;
}

select {
	width: 100%;
	padding: 12px 15px;
	border: 2px solid #dfe1e5;
	border-radius: 8px;
	font-size: 16px;
	background-color: white;
	transition: border-color 0.3s;
	box-sizing: border-box;
	appearance: none;
	background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24'%3E%3Cpath fill='%23333' d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 15px center;
}

select:focus {
	outline: none;
	border-color: var(--primary-color);
}

.drive-link {
	margin-top: 15px;
}

.drive-link a {
	display: inline-block;
	padding: 8px 15px;
	background: #4285f4;
	color: white;
	text-decoration: none;
	border-radius: 4px;
	font-weight: bold;
}

.drive-link a:hover {
	background: #3367d6;
}

.error-result {
	background-color: #fdeded;
	border: 1px solid #f8d7da;
}

.error-message {
	color: #dc3545;
	font-weight: 500;
}

@media (max-width: 768px) {

	.content-mode-selector,
	.form-options {
		grid-template-columns: 1fr;
		display: grid;
	}

	.format-group,
	.quality-group {
		margin-right: 0;
	}

	.cookies-file-actions {
		flex-direction: column;
	}

	.cookies-action-button {
		width: 100%;
	}
}
</style>
